// The Carrier Dashboard's own endpoints. The page reads TRIPS, not bookings: the server groups,
// filters and tallies, so a page of 20 trips is all that crosses the wire whatever date range is
// asked for. See oms/api/views/carrier_board.py.
import axios from "axios";
import { errorActions } from "../../../helpers/auth_helper";
import { LogisticsOrderRow } from "../types";
import { arrivalStatus, DriverPerformance } from "./driver_performance";

// Everything the three endpoints scope on. The row filters (vendor/driver/mile/delivery_state) are
// server-side now — they read derived values, so they could never have been SQL filters, and the
// server folds them in the same pass that builds the counts.
export interface CarrierScope {
  dispatch_from?: string;
  dispatch_to?: string;
  city?: string;
  warehouse_id?: number;
  express?: boolean;
  search?: string;
  vendor?: string;
  driver?: string;
  mile?: string;
  delivery_state?: string;
}

export interface CarrierTrip {
  key: string;
  trip_name: string | null;
  vendor: string | null;
  vehicle_number: string | null;
  driver_name: string | null;
  driver_number: string | null;
  stops: number; // AFTER filtering — a mile filter shrinks a mixed trip's stop count
  rows: LogisticsOrderRow[];
}

export interface CarrierTripsResponse {
  count: number; // trips in the whole scope, not this page
  bookings: number; // stops across all of them
  page: number | null;
  page_size: number | null;
  num_pages: number;
  next: number | null;
  previous: number | null;
  results: CarrierTrip[];
}

export type MetricKey = "shipments" | "delivered" | "incomplete";

export interface CarrierSummaryResponse {
  total: number;
  delivered: number;
  incomplete: number;
  trip_count: number;
  mile: Record<MetricKey, { first_mile: number; last_mile: number }>;
  breached: Record<MetricKey, number>;
  drivers: DriverPerformance[];
  vendors: string[]; // picker options, off the UNFILTERED scope
  drivers_available: string[]; // …narrowed by the chosen vendor only
}

export interface CarrierBookingsResponse {
  count: number;
  page: number | null;
  page_size: number | null;
  num_pages: number;
  results: LogisticsOrderRow[];
}

async function get<T>(url: string, params: object, message: string): Promise<T | undefined> {
  try {
    return await axios
      .get(url, { params })
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, message);
        return undefined;
      });
  } catch {
    return undefined;
  }
}

export const listCarrierTrips = (params: CarrierScope & { page?: number; page_size?: number }) =>
  get<CarrierTripsResponse>("/oms/logistics/trips/", params, "Error loading trips");

// The server's driver rows, before the arrival verdicts are folded in. `arrived_at` is the earliest
// milestone check-in Loginext pushed for that driver (see `carrier_board._fold_arrival`), or null
// when none landed; `days` is that same figure split per IST day, which is what a multi-day filter
// reads. Both verdicts are computed HERE rather than server-side because the cutoff rule lives in
// `driver_performance.ts` and must stay in one place.
interface DriverPerformancePayload
  extends Omit<DriverPerformance, "arrivedAt" | "status" | "days"> {
  arrived_at: string | null;
  days?: { day: string; arrived_at: string }[];
}

const toDriver = (d: DriverPerformancePayload): DriverPerformance => ({
  ...d,
  arrivedAt: d.arrived_at ?? null,
  status: arrivalStatus(d.arrived_at),
  // A day is only carried once it HAS a verdict: `arrivalStatus` returns null for an unparseable
  // stamp, and a day we cannot judge must not sit in the denominator of "3 of 5 days on time".
  days: (d.days ?? []).flatMap((x) => {
    const status = arrivalStatus(x.arrived_at);
    return status ? [{ day: x.day, arrivedAt: x.arrived_at, status }] : [];
  }),
});

// Deliberately a SEPARATE request from the trip page: it spans the whole filtered range, so
// folding it into the list would make a wide date range block the table behind it.
export const getCarrierSummary = async (params: CarrierScope) => {
  const res = await get<Omit<CarrierSummaryResponse, "drivers"> & {
    drivers: DriverPerformancePayload[];
  }>("/oms/logistics/trips/summary/", params, "Error loading summary");
  if (!res) return undefined;
  return { ...res, drivers: (res.drivers ?? []).map(toDriver) } as CarrierSummaryResponse;
};

// The Summary drill-down: one page of the bookings behind a tile.
export const listCarrierBookings = (
  params: CarrierScope & { metric?: string; sla?: string; page?: number; page_size?: number }
) => get<CarrierBookingsResponse>("/oms/logistics/trips/bookings/", params, "Error loading bookings");

// --- Our own tracking view -----------------------------------------------------------------
// The fallback for LogiNext's embedded page, which carries THEIR session and so shows a login
// screen to anyone not signed in to Loginext in this browser (or on Safari, which blocks the
// third-party cookies it needs by default). Every field below is ours, so this always renders.
export interface TrackingEndpoint {
  label: string | null;
  address: string | null;
  lat: number | null;
  lng: number | null;
}

export interface TrackingEvent {
  code: string;
  label: string;
  severity: "info" | "success" | "warning" | "error";
  occurred_at: string;
}

export interface ShipmentTracking {
  id: number;
  provider_ref: string | null;
  awb_no: string | null;
  delivery_status: string;
  delivered_at: string | null;
  eta: string | null;
  trip_name: string | null;
  vehicle_number: string | null;
  driver_name: string | null;
  driver_number: string | null;
  origin: TrackingEndpoint;
  destination: TrackingEndpoint;
  events: TrackingEvent[]; // oldest first — it reads as a story, not a log
}

export const getShipmentTracking = (docId: number) =>
  get<ShipmentTracking>(`/oms/logistics/${docId}/tracking/`, {}, "Could not load tracking");

// --- CSV export ---------------------------------------------------------------------------------
// The board, flattened to one row per shipment, over the WHOLE filtered range rather than the page
// on screen — see `CarrierTripExportView`. The server does the flattening: doing it in the browser
// would mean fetching every page first, and would give a file that disagrees with the Summary tab
// the moment a filter is derived server-side (which most of them now are).
//
// Downloaded as a BLOB rather than by pointing the browser at the URL: the endpoint needs the
// Authorization header, and a plain <a href> or window.open carries cookies, not our JWT.
export const downloadCarrierTripsCsv = async (params: CarrierScope): Promise<boolean> => {
  try {
    const res = await axios.get("/oms/logistics/trips/export/", {
      params,
      responseType: "blob",
    });
    // The server names the file (vendor + timestamp); fall back only if the header is stripped by a
    // proxy, which is the one thing about this that is out of our hands.
    const disposition = (res?.headers?.["content-disposition"] as string) || "";
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const filename = match?.[1] || `carrier-trips-${new Date().toISOString().slice(0, 10)}.csv`;

    const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Released on the next tick — revoking synchronously can cancel the download in Safari.
    setTimeout(() => window.URL.revokeObjectURL(url), 0);
    return true;
  } catch (error: any) {
    errorActions(error, "Could not download the CSV");
    return false;
  }
};
